import React, { useEffect } from "react";
import styles from "./NavBar.module.css";
import { Link, useLocation } from "react-router-dom";
import { useAuthStore } from "../../../features/auth/model/store";
import { useNavItems } from "../nav/navConfig";
import LogoutIcon from "../../assets/icons/navBar/LogoutIcon";
import { useRescheduleCount } from "../../../entities/reschedule";
import { useDockedPanelWidth } from "../../../shared/lib/dockedPanel";
import { PATHS } from "../../routes/paths";

interface Props {
  className?: string;
}

const LOCAL_STORAGE_KEY = "activeNavLink";

const NavBar: React.FC<Props> = ({ className }) => {
  const location = useLocation();
  const logout = useAuthStore((s) => s.logout);
  const navItems = useNavItems();
  const rescheduleCount = useRescheduleCount();
  const rescheduleBadge = rescheduleCount >= 100 ? "99+" : rescheduleCount > 0 ? String(rescheduleCount) : null;

  /**
   * Icon rail while a docked upsert panel shows (the disposition board's forms). The panel takes
   * 560px of the right edge; the labels are what the rail gives back. Every link keeps its name
   * through `aria-label` and `title`, so the rail reads and hovers the same as the full bar.
   */
  const isRail = useDockedPanelWidth() != null;

  // Remembered for the next session; the highlight itself is read off the router, not off this.
  useEffect(() => {
    localStorage.setItem(LOCAL_STORAGE_KEY, location.pathname);
  }, [location.pathname]);

  const isActive = (path: string) =>
    path === "/"
      ? location.pathname === "/"
      : location.pathname === path || location.pathname.startsWith(path + "/");

  const handleClick = (path: string) => {
    localStorage.setItem(LOCAL_STORAGE_KEY, path);
  };

  return (
    <nav
      aria-label="Hauptnavigation"
      className={`${className ?? ""} ${styles.navBar} ${isRail ? styles.rail : ""} navbar`}
    >
      {navItems.map(({ path, label, Icon }) => {
        const active = isActive(path);
        return (
          <Link
            key={path}
            to={path}
            onClick={() => handleClick(path)}
            className={`${styles.navLink} ${active ? styles.active : ""}`}
            aria-label={label}
            title={label}
          >
            {Icon && (
              <span className=" shrink-0">
                <Icon className={active ? "text-white" : "text-[#004494]"} aria-hidden />

              </span>
            )}
            <p>{label}</p>
            {path === PATHS.reschedule && rescheduleBadge !== null && (
              <span className="absolute -top-1 -right-1 flex items-center justify-center min-w-[30px] h-[30px] px-1 rounded-full bg-red-500 text-white text-[15px] font-bold leading-none">
                {rescheduleBadge}
              </span>
            )}
          </Link>
        );
      })}

      <button
        onClick={() => logout()}
        className={`${styles.navLink} ${styles.logoutButton}`}
        aria-label="Abmelden"
        title="Abmelden"
      >
        <LogoutIcon className="text-[#004494]" aria-hidden />
        <p>Abmelden</p>
      </button>
    </nav>
  );
};

export default NavBar;
